import { useState, useEffect, useCallback, useRef } from "react";
import { isAxiosError, isCancel } from "axios";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../../shared/api/queryClient";
import {
  TEMPLATE_JOB_TIMEOUT_MS,
  templateJobPollInterval,
  templateJobQuery,
  templateKeys,
} from "../../features/templates/api/templateQueries";
import { currentProfileQuery } from "../../features/users/api/profileQueries";
import { useOrgId } from "../../shared/auth/orgContext";
import { presignedClient, profileClient } from "@/shared/api/clients";
import {
  deleteTemplate,
  downloadTemplateBlob,
  fetchTemplatesRaw,
  initTemplateUpload,
  processTemplate,
  saveTemplate,
  templateErrorMessage,
} from "@/features/templates/api/templatesApi";
import { useTemplateChat } from "@/features/templates/hooks/useTemplateChat";
import { useToast } from "../../shared/toast";
import { validateFile, MAX_FILES } from "./utils";
import type { Template, UploadQueueItem, WorkflowStep } from "./types";

export function useTemplateUploadPage() {
  const { data: profileData } = useQuery(currentProfileQuery());
  const userProfile = profileData ?? null;
  const organizationId = useOrgId();

  const [stations, setStations] = useState<
    { stationId: string; stationName: string }[]
  >([]);
  const [stationsLoading, setStationsLoading] = useState(true);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(
    organizationId,
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [templateSearch, setTemplateSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [, setIsAtChatBottom] = useState(true);
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const prevMessageCountRef = useRef(0);

  // Workflow state
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>("upload");
  const [reviewContent, setReviewContent] = useState("");
  const [generatedExamples, setGeneratedExamples] = useState<string[]>([]);
  const [signOffDialogOpen, setSignOffDialogOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );
  const [editingName, setEditingName] = useState("");

  const toast = useToast();

  const templateChat = useTemplateChat({
    jobId,
    onTemplateUpdate: (newTemplate, newExamples) => {
      setReviewContent(newTemplate);
      setGeneratedExamples(newExamples);
    },
    showError: toast.error,
  });

  // Chat scroll helpers
  const scrollChatToBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, []);

  const checkIfAtBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  }, []);

  const handleChatScroll = useCallback(() => {
    const atBottom = checkIfAtBottom();
    setIsAtChatBottom(atBottom);
    if (atBottom) setShowScrollArrow(false);
  }, [checkIfAtBottom]);

  // Auto-scroll on new messages
  useEffect(() => {
    const count = templateChat.messages.length;
    const prev = prevMessageCountRef.current;
    prevMessageCountRef.current = count;

    if (count > prev && count > 0) {
      const lastMsg = templateChat.messages[count - 1];
      if (lastMsg.role === "user") {
        scrollChatToBottom();
      } else if (lastMsg.role === "assistant") {
        if (!checkIfAtBottom()) {
          setShowScrollArrow(true);
        } else {
          scrollChatToBottom();
        }
      }
    }
  }, [templateChat.messages, scrollChatToBottom, checkIfAtBottom]);

  // Sync selectedStationId when organizationId arrives
  useEffect(() => {
    if (organizationId) {
      setSelectedStationId(organizationId);
    }
  }, [organizationId]);

  // Fetch all stations
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await profileClient.get("/v1/stations");
        if (!cancelled && response.data?.stations?.length > 0) {
          setStations(
            response.data.stations.map((s: any) => ({
              stationId: s.id,
              stationName: s.name,
            })),
          );
        }
      } catch (err) {
        console.error("Failed to fetch stations:", err);
        if (!cancelled && organizationId && userProfile?.stationName) {
          setStations([
            { stationId: organizationId, stationName: userProfile.stationName },
          ]);
        }
      } finally {
        if (!cancelled) setStationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, userProfile?.stationName]);

  // Fetch templates
  const fetchTemplates = useCallback(
    async (showLoading = true) => {
      if (!selectedStationId) return;
      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      if (showLoading) setIsLoading(true);
      try {
        const data = (await fetchTemplatesRaw(
          selectedStationId,
          controller.signal,
        )) as { templates?: unknown[] } | unknown[] | undefined;
        const items = Array.isArray(data) ? data : (data?.templates ?? []);
        const mapped: Template[] = items
          .filter((item: any) => item.template)
          .map((item: any) => ({
            id: item.template.templateId,
            templateId: item.template.templateId,
            name: item.template.name,
            stationId: item.template.stationId,
            content: item.template.content,
            uploadedBy: item.template.uploadedBy,
            uploadedDate: item.template.createdAt,
            status: "active" as const,
          }));
        setTemplates(mapped);
      } catch (err: any) {
        if (isCancel(err)) return;
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "An error occurred while fetching templates. Please try again.";
        toast.error(errorMessage);
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [toast, selectedStationId],
  );

  // File handling
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    handleFilesAdd(Array.from(files));
    event.target.value = "";
  };

  const handleFilesAdd = (files: File[]) => {
    const currentCount = uploadQueue.length;
    if (currentCount + files.length > MAX_FILES) {
      toast.error(
        `You can upload a maximum of ${MAX_FILES} files. You already have ${currentCount} file${currentCount === 1 ? "" : "s"} queued.`,
      );
      return;
    }

    // Dedupe by name alone: the presigned-URL response is keyed by fileName,
    // so two same-name files would collide on one URL at submit time and
    // silently overwrite each other.
    const existingNames = new Set(uploadQueue.map((item) => item.file.name));
    const uniqueFiles = files.filter((file) => {
      if (existingNames.has(file.name)) return false;
      existingNames.add(file.name);
      return true;
    });

    if (uniqueFiles.length === 0) return;

    const newItems: UploadQueueItem[] = uniqueFiles.map((file) => {
      const validation = validateFile(file);
      return {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        progress: 0,
        status: validation.valid ? "pending" : "error",
        errorMessage: validation.error,
      };
    });

    setUploadQueue((prev) => [...prev, ...newItems]);

    newItems
      .filter((item) => item.status === "pending")
      .forEach((item) => {
        setUploadQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? { ...q, status: "completed", progress: 100 }
              : q,
          ),
        );
      });
  };

  const handleSubmitForProcessing = async () => {
    const stagedFiles = uploadQueue.filter((i) => i.status === "completed");
    if (stagedFiles.length === 0) return;

    // A previous job's terminal data may still be cached (the observer stays
    // mounted, so gcTime never fires) — without this clear, the transition
    // effect replays the stale failed/completed result against the new submit.
    queryClient.removeQueries({ queryKey: templateKeys.jobs() });
    setJobId(null);

    setWorkflowStep("processing");
    setProcessingError(null);

    try {
      // The presigned signature covers Content-Type, so the header on the PUT
      // must be the SAME STRING sent as contentType here or S3 answers 403.
      // Resolved once per file rather than recomputed at PUT time.
      const staged = stagedFiles.map((item) => ({
        file: item.file,
        contentType: item.file.type || "application/octet-stream",
      }));

      const { jobId: newJobId, uploads } = await initTemplateUpload(
        staged.map(({ file, contentType }) => ({
          fileName: file.name,
          fileSize: file.size,
          contentType,
        })),
      );

      const uploadsByName = new Map<string, string>(
        uploads.map((u) => [u.fileName, u.presignedUrl]),
      );
      await Promise.all(
        staged.map(({ file, contentType }) => {
          const presignedUrl = uploadsByName.get(file.name);
          if (!presignedUrl) {
            throw new Error(`No presigned URL returned for file: ${file.name}`);
          }
          // presignedClient carries no auth interceptor by design — an
          // Authorization header invalidates the URL signature.
          return presignedClient.put(presignedUrl, file, {
            headers: { "Content-Type": contentType },
          });
        }),
      );

      await processTemplate(newJobId);

      setJobId(newJobId);
    } catch (err: unknown) {
      setWorkflowStep("upload");
      // Not every failure here is a `{ error, details }` template-API body: the
      // missing-presigned-URL throw above is a plain Error, and a rejected S3
      // PUT answers with an XML body templateErrorMessage can't unwrap. Fall
      // back to the thrown message so those don't collapse into a bare
      // "Failed to submit files for processing".
      toast.error(
        templateErrorMessage(
          err,
          (err instanceof Error && err.message) ||
            "Failed to submit files for processing",
        ),
      );
    }
  };

  const handleRemoveFromQueue = (id: string) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== id));
  };

  // Drag and drop
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) handleFilesAdd(files);
  };

  // Delete
  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      const templateIdentifier =
        templateToDelete.id || templateToDelete.templateId;
      if (!templateIdentifier) {
        toast.error("Template identifier not found for deletion.");
        setIsDeleting(false);
        return;
      }

      await deleteTemplate({
        templateId: templateIdentifier,
        stationId: selectedStationId,
      });

      const deletedName = templateToDelete.fileName || templateToDelete.name;
      toast.success(`${deletedName ?? "Template"} deleted successfully`);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates(false);
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        "Failed to delete template. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Rename
  const handleRename = async (template: Template) => {
    const trimmed = editingName.trim();
    if (!trimmed || trimmed === template.name) {
      setEditingTemplateId(null);
      return;
    }
    try {
      await saveTemplate({
        template: {
          templateId: template.templateId,
          stationId: template.stationId ?? selectedStationId,
          name: trimmed,
          content: template.content,
          recordType: "Template",
        },
        examples: [],
      });
      setEditingTemplateId(null);
      fetchTemplates(false);
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? "Failed to rename template");
    }
  };

  // Sign off
  const handleSignOff = async () => {
    try {
      await saveTemplate({
        template: {
          stationId: selectedStationId,
          name: uploadQueue[0]?.file?.name ?? "Uploaded Template",
          content: reviewContent,
          uploadedBy: userProfile
            ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
            : undefined,
        },
        examples: generatedExamples,
      });
      toast.success("Template saved successfully");
      setSignOffDialogOpen(false);
      setWorkflowStep("complete");
      fetchTemplates(false);
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? "Failed to save template");
      setSignOffDialogOpen(false);
    }
  };

  // Download
  const handleDownload = async (template: Template) => {
    try {
      const templateIdentifier = template.id || template.templateId;
      if (!templateIdentifier) {
        toast.error("Template identifier not found for download.");
        return;
      }

      const response = await downloadTemplateBlob(templateIdentifier);

      const disposition = response.headers["content-disposition"] as
        | string
        | undefined;

      const { getFilenameFromDisposition } = await import("./utils");
      const downloadName = getFilenameFromDisposition(
        disposition,
        template.fileName || template.name || "template.txt",
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const templateName = template.fileName || template.name || "template";
      toast.success(`Download started for ${templateName}`);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        "Failed to download template. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Pagination
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Poll for template job completion. refetchIntervalInBackground matches
  // the old setInterval, which kept ticking in background tabs.
  const jobQuery = useQuery({
    ...templateJobQuery(jobId ?? ""),
    enabled: workflowStep === "processing" && !!jobId,
    refetchInterval: (query) =>
      templateJobPollInterval(query.state.data, query.state.error),
    refetchIntervalInBackground: true,
  });

  // Translate job-status transitions into the workflow state machine.
  useEffect(() => {
    const data = jobQuery.data;
    if (!data || workflowStep !== "processing") return;
    if (data.status === "completed" && data.result) {
      setReviewContent(data.result.template);
      setGeneratedExamples(data.result.generatedExamples ?? []);
      templateChat.resetChat();
      setWorkflowStep("review");
    } else if (data.status === "failed") {
      setWorkflowStep("upload");
      toast.error(data.error ?? "Template generation failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuery.data, workflowStep]);

  useEffect(() => {
    const err = jobQuery.error;
    if (!err || workflowStep !== "processing") return;
    // Network blip: the poll keeps running (templateJobPollInterval).
    if (isAxiosError(err) && !err.response) return;
    setWorkflowStep("upload");
    toast.error(
      templateErrorMessage(err, err.message || "Template processing failed"),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuery.error, workflowStep]);

  // Generation runs on a 10-minute Lambda, so a job still non-terminal past
  // that deadline will never resolve. Without this the page would poll a
  // dead job until its 24h row TTL turned the poll into a 404.
  useEffect(() => {
    if (workflowStep !== "processing" || !jobId) return;
    const timer = setTimeout(() => {
      setWorkflowStep("upload");
      toast.error(
        "Template generation timed out. Please try again with fewer or smaller files.",
      );
    }, TEMPLATE_JOB_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStep, jobId]);

  const filteredTemplates = templateSearch
    ? templates.filter((t) => {
        const search = templateSearch.toLowerCase();
        const name = (t.fileName || t.name || "").toLowerCase();
        return name.includes(search);
      })
    : templates;

  const paginatedTemplates = filteredTemplates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const resetWorkflow = () => {
    // Drop any cached job result so a reused page never replays a stale
    // "completed" transition.
    queryClient.removeQueries({ queryKey: templateKeys.jobs() });
    setWorkflowStep("upload");
    setUploadQueue([]);
    setReviewContent("");
    setGeneratedExamples([]);
    templateChat.resetChat();
    setJobId(null);
    setProcessingError(null);
  };

  return {
    // Station
    stations,
    stationsLoading,
    selectedStationId,
    setSelectedStationId,
    setTemplates,
    setPage,

    // Templates
    templates,
    filteredTemplates,
    paginatedTemplates,
    isLoading,
    page,
    rowsPerPage,
    templateSearch,
    setTemplateSearch,
    fetchTemplates,
    handleChangePage,
    handleChangeRowsPerPage,

    // Upload
    uploadQueue,
    isDragOver,
    fileInputRef,
    handleFileSelect,
    handleFilesAdd,
    handleSubmitForProcessing,
    handleRemoveFromQueue,
    handleDragOver,
    handleDragLeave,
    handleDrop,

    // Delete
    deleteDialogOpen,
    setDeleteDialogOpen,
    templateToDelete,
    isDeleting,
    handleDeleteClick,
    handleDeleteConfirm,

    // Rename
    editingTemplateId,
    setEditingTemplateId,
    editingName,
    setEditingName,
    handleRename,

    // Download
    handleDownload,

    // Preview
    previewTemplate,
    setPreviewTemplate,

    // Workflow
    workflowStep,
    setWorkflowStep,
    reviewContent,
    generatedExamples,
    processingError,
    setProcessingError,
    jobId,
    setJobId,
    resetWorkflow,

    // Sign off
    signOffDialogOpen,
    setSignOffDialogOpen,
    handleSignOff,

    // Chat
    templateChat,
    chatContainerRef,
    handleChatScroll,
    showScrollArrow,
    setShowScrollArrow,
    scrollChatToBottom,
  };
}
